/**
 * StepDots — Minimal step indicator (dots).
 * Parameterized: pass total + currentIndex. Active dot is filled red.
 *
 * Used by the cancel flow (3 steps) and reusable for future linear flows.
 */

import React from 'react';
import { cn } from '@/lib/utils';

interface StepDotsProps {
  total: number;
  currentIndex: number;
  /** Optional accessibility label, e.g. "Step 2 of 3". */
  ariaLabel?: string;
  className?: string;
}

export const StepDots: React.FC<StepDotsProps> = ({ total, currentIndex, ariaLabel, className }) => {
  return (
    <div
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={total}
      aria-valuenow={currentIndex + 1}
      aria-label={ariaLabel ?? `Step ${currentIndex + 1} of ${total}`}
      className={cn('flex items-center gap-1.5', className)}
    >
      {Array.from({ length: total }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-1.5 rounded-full transition-all',
            i === currentIndex
              ? 'w-6 bg-[#D72E16]'
              : i < currentIndex
                ? 'w-1.5 bg-white/60'
                : 'w-1.5 bg-white/15',
          )}
        />
      ))}
    </div>
  );
};
